/**
 * MK Slideshow — 包裹 Swiper 容器。可选 `data-*` 覆盖下方默认值（属性可省略）。
 * 依赖全局 `window.Swiper`（`swiper@12-min.js`）。
 *
 * data-per-view-pc        桌面（≥breakpoint）每屏幻灯片数，支持小数
 * data-per-view-mobile    移动端每屏幻灯片数
 * data-breakpoint         桌面断点 min-width（px）
 * data-autoplay           true / false（仅在为 false 时需写入）
 * data-autoplay-delay     自动播放间隔（毫秒）
 * data-mobile-carousel    移动端是否启用轮播（仅在为 false 时需写入）
 * data-space-between         幻灯片间距（px），移动端与桌面共用；可与下面两项组合，未写分项时回退到本值
 * data-space-between-mobile  仅移动端（< breakpoint）间距，缺省则用 data-space-between / 默认
 * data-space-between-pc      仅桌面（≥ breakpoint）间距，缺省则用 data-space-between / 默认
 */
const MK_SLIDESHOW_DEFAULTS = Object.freeze({
  perViewPc: 1,
  perViewMobile: 1,
  breakpoint: 768,
  autoplay: false,
  autoplayDelay: 5000,
  mobileCarousel: true,
  spaceBetween: 0,
  spaceBetweenMobile: 0,
  spaceBetweenPc: 0,
});

class MKSlideshow extends HTMLElement {
  /** @type {InstanceType<typeof window.Swiper> | null} */
  #swiper = null;
  /** @type {MediaQueryList | null} */
  #breakpointMq = null;
  /** @type {((event?: MediaQueryListEvent) => void) | null} */
  #handleViewportChange = null;

  static get defaults() {
    return MK_SLIDESHOW_DEFAULTS;
  }

  connectedCallback() {
    this.#initWhenReady();
  }

  disconnectedCallback() {
    this.#destroy();
  }

  /** @param {string | undefined} raw @param {number} fallback */
  #num(raw, fallback) {
    const n = parseFloat(String(raw ?? '').trim());
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  /** @param {string | undefined} raw @param {boolean} fallback */
  #bool(raw, fallback) {
    if (raw == null || raw === '') return fallback;
    const s = String(raw).toLowerCase();
    if (s === 'true' || s === '1') return true;
    if (s === 'false' || s === '0') return false;
    return fallback;
  }

  /** @param {string | undefined} raw @param {number} fallback */
  #intMs(raw, fallback) {
    const n = parseInt(String(raw ?? '').trim(), 10);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  }

  /** @param {string | undefined} raw @param {number} fallback 允许 0 */
  #nonNegative(raw, fallback) {
    const n = parseFloat(String(raw ?? '').trim());
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  }

  #buildSwiperOptions() {
    const d = MK_SLIDESHOW_DEFAULTS;
    const perViewMobile = this.#num(this.dataset.perViewMobile, d.perViewMobile);
    const perViewPc = this.#num(this.dataset.perViewPc, d.perViewPc);
    const breakpoint = Math.max(0, Math.round(this.#num(this.dataset.breakpoint, d.breakpoint)));
    const mobileCarousel = this.#bool(this.dataset.mobileCarousel, d.mobileCarousel);
    const autoplayOn = this.#bool(this.dataset.autoplay, d.autoplay);
    const autoplayDelay = this.#intMs(this.dataset.autoplayDelay, d.autoplayDelay);
    const spaceShared = this.#nonNegative(this.dataset.spaceBetween, d.spaceBetween);
    const spaceMobile = this.#nonNegative(this.dataset.spaceBetweenMobile, spaceShared);
    const spacePc = this.#nonNegative(this.dataset.spaceBetweenPc, spaceShared);

    const autoplayConfig = autoplayOn
      ? { delay: autoplayDelay, disableOnInteraction: false }
      : false;

    const bpKey = String(breakpoint);

    return {
      slidesPerView: perViewMobile,
      spaceBetween: spaceMobile,
      loop: true,
      enabled: mobileCarousel,
      autoplay: mobileCarousel ? autoplayConfig : false,
      breakpoints: {
        [bpKey]: {
          enabled: true,
          slidesPerView: perViewPc,
          spaceBetween: spacePc,
          autoplay: autoplayConfig,
        },
      },
    };
  }

  /**
   * 当移动端关闭轮播时，强制平铺展示 slide。
   * @param {number} breakpoint
   * @param {boolean} mobileCarousel
   */
  #setupMobileFlatLayoutSync(breakpoint, mobileCarousel) {
    if (mobileCarousel) return;

    const flatGap = this.#nonNegative(this.dataset.spaceBetween, MK_SLIDESHOW_DEFAULTS.spaceBetween);

    this.#breakpointMq = window.matchMedia(`(max-width: ${Math.max(0, breakpoint - 1)}px)`);
    this.#handleViewportChange = () => {
      const root = this.querySelector('.swiper');
      const wrapper = root?.querySelector('.swiper-wrapper');
      if (!root || !wrapper) return;
      if (!(root instanceof HTMLElement)) return;
      if (!(wrapper instanceof HTMLElement)) return;

      const isMobile = this.#breakpointMq?.matches === true;
      const slides = Array.from(wrapper.children);

      if (isMobile) {
        root.style.overflow = 'visible';
        wrapper.style.transform = 'none';
        wrapper.style.display = 'grid';
        wrapper.style.gridTemplateColumns = '1fr';
        wrapper.style.rowGap = `${flatGap}px`;
        wrapper.style.columnGap = `${flatGap}px`;
        for (const slide of slides) {
          if (!(slide instanceof HTMLElement)) continue;
          slide.style.width = '100%';
          slide.style.marginRight = '0px';
        }
      } else {
        root.style.removeProperty('overflow');
        wrapper.style.removeProperty('transform');
        wrapper.style.removeProperty('display');
        wrapper.style.removeProperty('grid-template-columns');
        wrapper.style.removeProperty('row-gap');
        wrapper.style.removeProperty('column-gap');
        for (const slide of slides) {
          if (!(slide instanceof HTMLElement)) continue;
          slide.style.removeProperty('width');
          slide.style.removeProperty('margin-right');
        }
      }
    };

    this.#handleViewportChange();
    this.#breakpointMq.addEventListener('change', this.#handleViewportChange);
  }

  #initWhenReady() {
    const run = () => {
      const root = this.querySelector('.swiper');
      if (!root || this.#swiper) return;

      const Swiper = window.Swiper;
      if (typeof Swiper !== 'function') {
        window.addEventListener('load', run, { once: true });
        return;
      }

      const baseOptions = this.#buildSwiperOptions();
      const paginationEl = root.querySelector('.swiper-pagination');
      const options = {
        ...baseOptions,
        ...(paginationEl instanceof HTMLElement
          ? {
              pagination: {
                el: paginationEl,
                clickable: true,
              },
            }
          : {}),
      };
      this.#swiper = new Swiper(root, options);
      this.#setupMobileFlatLayoutSync(
        Number(this.dataset.breakpoint ?? MK_SLIDESHOW_DEFAULTS.breakpoint),
        this.#bool(this.dataset.mobileCarousel, MK_SLIDESHOW_DEFAULTS.mobileCarousel)
      );
    };

    run();
  }

  #destroy() {
    if (this.#breakpointMq && this.#handleViewportChange) {
      this.#breakpointMq.removeEventListener('change', this.#handleViewportChange);
    }
    this.#breakpointMq = null;
    this.#handleViewportChange = null;
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }
}

if (!customElements.get('mk-slideshow')) {
  customElements.define('mk-slideshow', MKSlideshow);
}
